import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { Anchor, Text, ThemeIcon, Title } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import {
  IconCircleCheckFilled,
  IconCirclePlus,
  IconKey,
  IconLogin2,
} from '@tabler/icons-react'
import { useResetPasswordMutation } from 'auth/api'
import PasswordStrengthIndicator from 'auth/components/PasswordStrengthIndicator'
import { usePasswordStrength } from 'auth/hooks'
import { AuthLoginStep, IResetPasswordFormValues } from 'auth/models'
import { setIsLoginPageUiCenter, setLoginStep } from 'auth/store'
import { useDispatch, useForm } from 'common/hooks'
import { Button, PasswordInput } from 'common/lib'
import { ROUTES, RoutesEnum } from 'services'
import { object, string } from 'yup'

interface IProps {
  token: string
}

export default function ResetPasswordForm({ token }: IProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const matches = useMediaQuery('(min-width: 768px)')
  const { mutate: resetPassword, isPending } = useResetPasswordMutation()
  const [isPasswordChanged, setIsPasswordChanged] = useState(false)

  const { form } = useForm<IResetPasswordFormValues>({
    defaultValues: {
      password: '',
      rePassword: '',
    },
    resolver: yupResolver(
      object({
        password: string().required(),
        rePassword: string()
          .test(
            'isPasswordTheSame',
            t('validation.incorrectPasswords'),
            (value, context) => {
              const { password } = context.parent

              return value === password
            },
          )
          .required(),
      }),
    ),
  })

  const formValues = useMemo(() => form, [form])

  // Get password values for validation
  const passwordValue = form.watch('password')
  const rePasswordValue = form.watch('rePassword')
  const passwordStrength = usePasswordStrength(passwordValue)

  const handleSubmit = useCallback(
    (values: IResetPasswordFormValues) => {
      resetPassword(
        { resetPasswordToken: token, password: values.password },
        {
          onSuccess: () => {
            setIsPasswordChanged(true)
            dispatch(setIsLoginPageUiCenter({ isLoginPageUiCenter: true }))
          },
        },
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resetPassword, token],
  )

  const renderChangedPasswordContent = useCallback(() => {
    if (matches) {
      return (
        <div
          className={'max-w-[450px] mt-[60px] flex flex-col justify-center items-center'}
        >
          <ThemeIcon size={108} variant={'white'}>
            <IconCircleCheckFilled
              className={'text-[#0090FE]'}
              height={108}
              width={108}
            />
          </ThemeIcon>
          <Title className={'font-medium mt-6 mb-4 text-4xl text-center'} order={1}>
            {t('auth.title.resetPasswordSuccess')}
          </Title>
          <Text className={'text-center heading-[19px]'}>
            {t('auth.title.resetPasswordText')}
          </Text>
          <div className={'w-full mt-8 flex justify-center'}>
            <Button
              leftSection={<IconLogin2 size={32} />}
              onClick={() => navigate(ROUTES.login())}
              size={'large'}
              type={'submit'}
            >
              {t('auth.control.signIn')}
            </Button>
          </div>
        </div>
      )
    }

    return (
      <div className={'w-full flex flex-col items-center justify-between'}>
        <div>
          <Title className={'font-bold text-[22px] leading-[33px] text-center'} order={1}>
            {t('auth.title.resetPasswordSuccess')}
          </Title>
          <Text className={'text-center text-[22px]'}>
            {t('auth.title.resetPasswordText')}
          </Text>
          <div
            className={
              'w-[100vw] h-[100vw] max-w-[300px] max-h-[300px] rounded-[50%] p-[60px] mt-[20px] flex items-center justify-center bg-green-100'
            }
          >
            <div
              className={
                'w-full h-full rounded-[50%] flex items-center justify-center bg-green-200'
              }
            >
              <IconCircleCheckFilled className={'text-green-600'} size={80} />
            </div>
          </div>
        </div>
        <div className={'w-full mt-8 flex justify-center'}>
          <Button
            leftSection={<IconLogin2 size={32} />}
            onClick={() => navigate(ROUTES.login())}
            size={'large'}
            type={'submit'}
          >
            {t('auth.control.signIn')}
          </Button>
        </div>
      </div>
    )
  }, [matches, navigate, t])

  useEffect(() => {
    setIsPasswordChanged(false)
    dispatch(setLoginStep({ step: AuthLoginStep.RESET_PASSWORD }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className={`flex flex-col justify-start w-full ${
        isPasswordChanged ? 'items-center' : ''
      }`}
    >
      {isPasswordChanged ? (
        renderChangedPasswordContent()
      ) : (
        <>
          <Title
            className={
              'font-medium mb-4 md:mb-4 md:mt-14 text-4xl text-center md:text-left'
            }
            order={1}
          >
            {t('auth.title.resetPassword')}
          </Title>
          <Title
            className={'font-medium mb-4 text-2xl text-center md:text-left'}
            order={2}
          >
            {t('auth.title.resetPasswordSub')}
          </Title>
          <FormProvider {...formValues}>
            <form
              className={'flex flex-col gap-6'}
              onSubmit={form.handleSubmit(handleSubmit)}
            >
              <div className={'flex flex-col gap-4'}>
                {/* Desktop: password i repassword obok siebie */}
                <div className={'hidden md:flex gap-4'}>
                  <div className={'flex-1'}>
                    <Controller
                      control={form.control}
                      name={'password'}
                      render={({
                        field: { value, onBlur, onChange },
                        fieldState: { error },
                      }) => (
                        <div>
                          <PasswordInput
                            classNames={{ root: 'w-full' }}
                            error={error?.message}
                            isDisabled={isPending}
                            label={t('auth.field.password')}
                            onBlur={onBlur}
                            onChange={onChange}
                            value={value}
                          />
                          <PasswordStrengthIndicator password={value} />
                        </div>
                      )}
                    />
                  </div>
                  <div className={'flex-1'}>
                    <Controller
                      control={form.control}
                      name={'rePassword'}
                      render={({
                        field: { value, onBlur, onChange },
                        fieldState: { error },
                      }) => (
                        <PasswordInput
                          classNames={{ root: 'w-full' }}
                          error={error?.message}
                          isDisabled={isPending}
                          label={t('auth.field.rePassword')}
                          onBlur={onBlur}
                          onChange={onChange}
                          value={value}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Mobile: password → PasswordStrengthIndicator → repassword */}
                <div className={'flex md:hidden flex-col gap-4'}>
                  <Controller
                    control={form.control}
                    name={'password'}
                    render={({
                      field: { value, onBlur, onChange },
                      fieldState: { error },
                    }) => (
                      <div>
                        <PasswordInput
                          classNames={{ root: 'w-full' }}
                          error={error?.message}
                          isDisabled={isPending}
                          label={t('auth.field.password')}
                          onBlur={onBlur}
                          onChange={onChange}
                          value={value}
                        />
                        <PasswordStrengthIndicator password={value} />
                      </div>
                    )}
                  />
                  <Controller
                    control={form.control}
                    name={'rePassword'}
                    render={({
                      field: { value, onBlur, onChange },
                      fieldState: { error },
                    }) => (
                      <PasswordInput
                        classNames={{ root: 'w-full' }}
                        error={error?.message}
                        isDisabled={isPending}
                        label={t('auth.field.rePassword')}
                        onBlur={onBlur}
                        onChange={onChange}
                        value={value}
                      />
                    )}
                  />
                </div>
              </div>
              <div
                className={
                  'flex flex-col items-start gap-6 md:items-center md:flex-row md:justify-between'
                }
              >
                <Text>
                  {t('auth.matchPasswords')}{' '}
                  <Anchor
                    className={
                      'font-medium no-underline focus:no-underline focus:outline-none text-[#082E9D] hover:text-[#082E9D] focus:text-[#082E9D] md:text-[#0090FE] md:hover:text-[#082E9D] md:focus:text-[#082E9D]'
                    }
                    component={Link}
                    to={RoutesEnum.LOGIN}
                  >
                    {t('auth.backToLoginPage')}
                  </Anchor>
                </Text>
              </div>
              <div className={'flex justify-center md:justify-start'}>
                <Button
                  isDisabled={!passwordStrength.isValid || !rePasswordValue || isPending}
                  leftSection={
                    matches ? <IconKey size={24} /> : <IconCirclePlus size={24} />
                  }
                  size={'large'}
                  type={'submit'}
                >
                  {matches ? t('auth.control.setNewPassword') : t('auth.control.create')}
                </Button>
              </div>
            </form>
          </FormProvider>
        </>
      )}
    </div>
  )
}
